import * as React from 'react'
import { Link } from 'react-router'
import { Loader2 } from 'lucide-react'
import { passwordResetRequestInputSchema } from '@lib/input/tenant-auth.input'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { PASSWORD_RESET_AUDIENCES, type PasswordResetAudience } from '@/auth/passwordResetAudience'

/**
 * Request a reset link. The confirmation is the same whether or not the email
 * has an account, so this page can't be used to check who has one.
 */
export function ForgotPasswordPage({ audience = 'dashboard' }: { audience?: PasswordResetAudience }) {
  const target = PASSWORD_RESET_AUDIENCES[audience]
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = passwordResetRequestInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError('Enter a valid email address.')
      return
    }
    setIsSubmitting(true)
    try {
      await target.request(parsed.data)
      setSent(true)
    } catch (failure) {
      setError(apiErrorMessage(failure))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <p className="text-body text-muted-foreground">
          If {email} has {target.account}, a reset link is on its way. It works once and expires in
          an hour.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to={target.loginPath}>Back to sign in</Link>
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Reset your password" description="We'll email you a link to choose a new one.">
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        {error ? <ErrorState message={error} /> : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          Send reset link
        </Button>
        <p className="text-center text-body">
          <Link to={target.loginPath} className="text-link underline-offset-4 hover:underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
